<template>
  <AuthLayout>
    <template #form>
      <LoadingScreen v-if="googleLoading" :color="authLoaderColor" />

      <div v-else class="login-form">
        <!-- Logo and Title -->
        <AuthFormHeader :title="headerTitle" :subtitle="headerSubtitle" />

        <!-- Social Login Section -->
        <div class="auth-social-section">
          <GoogleButton v-if="showGoogleLogin" @success="onGoogleLoginSuccess" @failure="onGoogleLoginFailure" />
          <GitHubButton @click="handleGitHubLogin" />
          <SSOButton @click="loginWithSSO" />

          <!-- Divider -->
          <div class="auth-divider">
            <div class="divider-line"></div>
            <span class="divider-text">or</span>
            <div class="divider-line"></div>
          </div>

          <!-- Email/Password Form -->
          <form @submit.prevent="login" class="auth-form" novalidate>
            <div>
              <an-input
                label="Email address"
                type="email"
                v-model="email"
                :invalid="!validEmail"
                placeholder="name@email.com"
                :theme="theme"
                size="lg"
                autocomplete="email"
                data-cy="login-email"
              />
              <div v-if="!validEmail && emailTouched" class="auth-field-error" data-cy="login-email-error">
                Please enter a valid email address
              </div>
            </div>

            <div>
              <an-input
                label="Password"
                type="password"
                v-model="password"
                :invalid="!validPassword"
                placeholder="Type your password here"
                :theme="theme"
                size="lg"
                autocomplete="current-password"
                data-cy="login-password"
              />
              <div v-if="!validPassword && passwordTouched" class="auth-field-error" data-cy="login-password-error">
                Password must contain at least 8 characters
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="errorMessage" class="auth-error">
              <svg-icon name="info" fill="var(--red)" />
              <span>{{ errorMessage }}</span>
            </div>

            <an-button
              type="submit"
              variant="purple"
              size="lg"
              :isWorking="loading"
              :rounded="false"
              data-cy="login-submit"
            >
              Sign In
            </an-button>
          </form>
        </div>

        <!-- Links Section -->
        <div class="auth-links">
          <div class="auth-link-group">
            <span class="auth-links-text">Don't have an account?</span>
            <an-link @click="$router.push({ name: 'signup', query: $route.query })" class="auth-link-signup">
              Sign Up
            </an-link>
          </div>
          <an-link @click="forgotPassword" class="auth-link-forgot">Forgot password?</an-link>
        </div>
      </div>
    </template>
  </AuthLayout>
</template>

<script>
import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
import LoadingScreen from '@/components/Loading/LoadingScreen';
import GoogleButton from '@/components/Button/GoogleButton';
import GitHubButton from '@/components/Button/GitHubButton';
import SSOButton from '@/components/Button/SSOButton';
import AuthLayout from '@/components/Auth/AuthLayout';
import AuthFormHeader from '@/components/Auth/AuthFormHeader';
import { openLink } from '@/components/OmniView/utils';
import { AuthBrandMixin, SignupMixin } from '@/mixins';

import auth from '@/auth';
import { toastError } from '@/services/bus';
import errorHandler from '@/services/errorHandler';
import { isValidEmail, normalizeEmail } from '@/utils/email';

export default {
  data() {
    return {
      email: '',
      password: '',
      errorMessage: '',
      loading: false,
      googleLoading: false,
      agreeWithTos: false,
      termsUrl: 'https://www.animaapp.com/tos/TOS.pdf',
      privacyUrl: 'https://support.animaapp.com/en/articles/8120546-anima-privacy-policy-explained',
      showGoogleLogin: !process.env.ORGANIZATION_NAME,
      validEmail: true,
      validPassword: true,
      emailTouched: false,
      passwordTouched: false
    };
  },
  mixins: [AuthBrandMixin, SignupMixin],
  components: {
    GoogleButton,
    GitHubButton,
    SSOButton,
    LoadingScreen,
    AuthLayout,
    AuthFormHeader
  },
  mounted() {
    this.$trackEvent('webapp.login');
    this.applyAuthTheme();
    if (window && window.top && window.top.postMessage) {
      //if it's chrome extension we will send a message so we can send the user to login
      window.top.postMessage('needs-to-login', '*');
    }
  },
  beforeDestroy() {
    this.removeTheme();
  },
  computed: {
    ...mapGetters({ firstName: 'users/firstName' }),
    ...mapState('devices', { currentDevice: 'currentItem' }),
    // A claim brought the reader here to move one artifact, so the header says so.
    headerTitle() {
      return this.isAgentGridClaimFlow ? 'Sign in to claim your artifact' : 'Welcome back';
    },
    headerSubtitle() {
      return this.isAgentGridClaimFlow
        ? 'Log in to move it into one of your workspaces.'
        : 'Log in to your account to continue building.';
    }
  },
  methods: {
    ...mapActions({
      getUser: 'users/fetchOne',
      createTeam: 'teams/create',
      updateUser: 'users/update'
    }),
    ...mapMutations({
      setCurrentTeam: 'teams/setCurrentItem'
    }),
    async handleGitHubLogin() {
      const { query } = this.$route;
      const { platform = 'web' } = query;

      const state = await auth.signGithubMessage({
        platform,
        ...(query.redirect ? { redirect: query.redirect } : {}),
        ...(query.pt ? { pt: query.pt } : {})
      });

      const searchParams = new URLSearchParams({
        client_id: process.env.GITHUB_CLIENT_ID,
        state,
        scope: ['read:user', 'user:email'].join(' ')
      });

      const url = new URL(`https://github.com/login/oauth/authorize?${searchParams.toString()}`);

      openLink({ link: url.href });
    },
    async onGoogleLoginSuccess(googleUser) {
      try {
        this.googleLoading = true;
        // We don't have explicit "agree with tos" checkbox on login page, but maybe we should?
        // But then, the user would have to click it every time they want to log in with google, because we wouldn't know if
        // they are existing user or not.
        await this.onGoogleSuccess({ ...googleUser, agreeWithTos: true });
      } catch (error) {
        this.googleLoading = false;
        errorHandler.captureException(error);
        toastError("We couldn't log you in with your Google account :(");
      }
    },
    async onGoogleLoginFailure(err) {
      errorHandler.captureExceptionAndTrack(err, { name: 'login.google-login.failure', cta: 'google-button' });
      toastError("We couldn't log you in with your Google account :(");
    },
    forgotPassword() {
      this.$router.replace({ name: 'forgot-password', query: this.$route.query });
    },
    loginWithSSO() {
      this.$trackEvent('login.sso-button.click');
      this.$router.push({ name: 'sso-login', query: this.$route.query });
    },
    validateEmail() {
      const email = normalizeEmail(this.email);
      this.validEmail = isValidEmail(email) && email.length > 0;
      this.emailTouched = true;
    },
    validatePassword() {
      this.validPassword = this.password.length >= 8;
      this.passwordTouched = true;
    },
    validateForm() {
      this.validateEmail();
      this.validatePassword();
      const isValid = this.validEmail && this.validPassword;
      if (!isValid) {
        this.$trackEvent('login.login.invalid-form', {
          is_email_valid: this.validEmail,
          is_password_valid: this.validPassword,
          email: this.email
        });
      }
      return isValid;
    },
    async login() {
      try {
        this.$trackEvent('login.login-button.click', {
          cta: 'anima'
        });

        this.errorMessage = '';

        if (!this.validateForm()) {
          return;
        }

        this.loading = true;

        const { access_token: token } = await auth.login(this.email, this.password);
        localStorage.setItem('token', token);

        const user = await this.getUser({ id: 'me', skipCache: true });

        this.$tracking.setUserEmail(user.email);
        this.$tracking.setUserId(user.id);
        this.$tracking.alias(user.id);

        this.$trackEvent('login.login.success', { cta: 'anima', device_id: this.currentDevice.device_id });

        await this.redirectAfterAuth(true);
      } catch (error) {
        if (error?.response?.status == 401) {
          this.errorMessage = 'Incorrect email, password or both.';
        } else if (error?.response?.status == 403) {
          this.errorMessage = error?.response?.data?.message || 'We failed to log you in! Please try again.';
        } else {
          this.errorMessage = 'We failed to log you in! Please try again.';

          errorHandler.captureExceptionAndTrack(error, {
            name: 'login.login.failure',
            data: { cta: 'anima', message: error?.response?.data?.message || error?.message, statusCode: error?.status }
          });
        }
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/_mixins.scss';
@import '@/styles/_auth.scss';
@import '@/styles/_auth-tokens.scss';

.login-form {
  display: flex;
  flex-direction: column;
  width: 100%;

  > :first-child {
    margin-bottom: $auth-gap-xl;
  }

  > .auth-social-section {
    margin-bottom: $auth-gap-sm;
  }
}

// Login-specific overrides for shared auth styles
.auth-form {
  .auth-checkbox {
    margin-top: $auth-gap-xs; // Total gap of 24px (16px base + 8px)
  }
}

.auth-field-error {
  margin-top: 8px;
  text-align: left;
  font-size: 14px;
  color: var(--red);
}

.auth-error {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: rgba(255, 98, 80, 0.1);
  border-radius: 6px;
  color: var(--red);
  font-size: 14px;
}

.auth-links {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-size: $auth-text-size;
  gap: $auth-gap-xs;

  .auth-link-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .auth-links-text {
    color: var(--transparent-white-50);
  }

  // Login-specific link styles (primary white signup, secondary forgot)
  .auth-link-signup {
    color: var(--white) !important;
    font-family: $auth-body-font;
    font-size: $auth-text-size;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
    transition: color 0.2s ease;

    &:hover {
      color: var(--transparent-white-80) !important;
      font-weight: 400 !important;
    }
  }

  .auth-link-forgot {
    color: var(--transparent-white-50) !important;
    font-family: $auth-body-font;
    font-size: $auth-text-size;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
    transition: color 0.2s ease;

    &:hover {
      color: var(--transparent-white-70) !important;
      font-weight: 400 !important;
    }
  }
}
</style>
